import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { jobStatusData, JobStatusRes } from './data/jobStatus';
import DataTable from '../../../../components/Common/UI/DataTable';
import Icons from '../../../../components/Common/Icons/Icons';
import PageSubHeader from '../../Common/PageSubHeader';
import { TabProps, Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/Common/UI/Tabs';
import { Input } from '../../../../components/Common/UI/Input';
import { jobColumns, jobSubRowsColumns } from './columns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/Common/UI/Select';
import { enumToOptionsArray } from '../../../../utils';
import { StatusesEnum, StatusesType } from '../../../types';
import DatePicker from '../../../../components/Common/UI/DataPicker';
import { initialPeriod, IPeriod } from '../../../../Guidance/components/Pages/Notices';
import { notifySuccess } from '../../../../components/Common/UI/Notify';

enum ExportJobsOptionsEnum {
  combined = 'Export Combined Jobs',
  paper = 'Export Paper Jobs',
  electronic = 'Export Electronic Jobs',
}

enum ExportJobsNotifyMessageEnum {
  combined = 'Combined Jobs Exported',
  paper = 'Paper Jobs Exported',
  electronic = 'Electronic Jobs Exported',
}

const expandedRowRender = (row: JobStatusRes | undefined) => {
  if (!row) {
    return <div>No data</div>;
  }

  const subRows = row.subRows.map(subRow => ({ ...subRow, jobId: row.id }));

  return (
    <div className="pl-6">
      <DataTable columns={jobSubRowsColumns} data={subRows} variant="subTable" />
    </div>
  );
};

function JobStatus() {
  const [jobData, setJobData] = useState<JobStatusRes[]>([]);

  useEffect(() => {
    setJobData(jobStatusData);
  }, [jobStatusData]);

  const [statusSelect, setStatusSelect] = useState<StatusesType | ''>('');
  const [jobIdSearch, setJobIdSearch] = useState<string>('');

  const handleFilter = (by: 'status' | 'jobNum', filterValue: string) => {
    if (by === 'status') {
      setStatusSelect(filterValue as StatusesType);
      // setJobIdSearch('');

      // If new value is empty set back all data
      if (filterValue) {
        const filteredData = jobStatusData.filter(job => job.status === (filterValue as StatusesType));

        setJobData(filteredData);
      } else {
        setJobData(jobStatusData);
      }
    } else if (by === 'jobNum') {
      setJobIdSearch(filterValue);
      setStatusSelect('');
      const filteredData = jobStatusData.filter(job => job.jobNum.toString().includes(filterValue));

      setJobData(filteredData);
    }
  };

  const [selectedPeriod, setSelectedPeriod] = useState<IPeriod>(initialPeriod);

  const handleChangeDate = (date: Date | undefined, field: keyof typeof initialPeriod) => {
    setSelectedPeriod(prevState => ({
      ...prevState,
      [field]: date,
    }));
  };

  const tabs: TabProps[] = [
    {
      value: 'all',
      label: 'All',
      content: (
        <DataTable columns={jobColumns} data={jobData} expandable={{ expandedRowRender, expandOnAction: 'button' }} />
      ),
    },
    {
      value: 'paper',
      label: 'Paper',
      content: (
        <DataTable
          columns={jobColumns}
          data={jobData.filter(item => item.type === 'paper')}
          expandable={{ expandedRowRender, expandOnAction: 'button' }}
        />
      ),
    },
    {
      value: 'eStatement',
      label: 'eStatement',
      content: (
        <DataTable
          columns={jobColumns}
          data={jobData.filter(item => item.type === 'eStatement')}
          expandable={{ expandedRowRender, expandOnAction: 'button' }}
        />
      ),
    },
    {
      value: 'pendingApproval',
      label: (
        <div className="flex items-center gap-2">
          <span>Pending Approval</span>
          <div className="bg-amber-600 text-white text-xs font-bold py-0.5 px-2 rounded-full">
            {jobData.filter(item => item.type === 'pendingApproval').length}
          </div>
        </div>
      ),
      content: (
        <DataTable
          columns={jobColumns}
          data={jobData.filter(item => item.type === 'pendingApproval')}
          expandable={{ expandedRowRender, expandOnAction: 'button' }}
        />
      ),
    },
  ];

  return (
    <div>
      <PageSubHeader
        leftContent={
          <div className="flex items-center gap-2">
            <Icons.JobStatus className="size-6" />
            <span>Job Status</span>
          </div>
        }
        rightContent={
          <div className="flex flex-wrap items-center gap-2">
            <DatePicker
              prefix="From"
              date={dayjs(selectedPeriod.from).toDate()}
              className="font-normal bg-addition6-5 sm:w-auto w-full"
              icon={<Icons.Calendar2 className="ml-[15px] opacity-40" />}
              onSelect={(date?: Date) => {
                handleChangeDate(date, 'from');
              }}
            />

            <Icons.ArrowLeft className="sm:block hidden" />
            <DatePicker
              prefix="To"
              icon={<Icons.Calendar2 className="ml-[15px] opacity-40" />}
              className="font-normal bg-addition6-5 sm:w-auto w-full"
              date={dayjs(selectedPeriod.to).toDate()}
              onSelect={(date?: Date) => {
                handleChangeDate(date, 'to');
              }}
            />
          </div>
        }
        breadcrumbs={[
          { path: '/infoTrac/', label: <Icons.HomeOutlined />, disableBg: true },
          { path: '/infoTrac', label: 'InfoTRAC' },
          { path: '/infoTrac/job-status', label: 'Job Status' },
        ]}
      />

      <Tabs defaultValue="all" className="">
        <div className="page-heading px-4 py-2 flex items-center justify-between bg-slate-100">
          <TabsList>
            {tabs.map(({ value, label }) => (
              <TabsTrigger key={`${value}-trigger`} value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex items-center gap-4">
            <Select
              value=""
              onValueChange={(val: keyof typeof ExportJobsNotifyMessageEnum) =>
                notifySuccess(ExportJobsNotifyMessageEnum[val])
              }
            >
              <SelectTrigger className="w-full min-w-44" variant="white">
                <SelectValue
                  placeholder={
                    <span className="flex gap-1">
                      <Icons.DownloadFilled />
                      Export Jobs to CSV
                    </span>
                  }
                />
              </SelectTrigger>

              <SelectContent>
                {enumToOptionsArray(ExportJobsOptionsEnum).map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    <span className="flex gap-1">
                      <Icons.DownloadFilled />
                      {label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusSelect} onValueChange={(val: StatusesType) => handleFilter('status', val)}>
              <SelectTrigger className="w-full min-w-44" variant="white" onClear={() => handleFilter('status', '')}>
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>

              <SelectContent>
                {enumToOptionsArray(StatusesEnum).map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Search by Job ID"
              icon={<Icons.Search />}
              variant="white"
              inputClassName="min-w-40"
              value={jobIdSearch}
              onChange={event => handleFilter('jobNum', event.target.value)}
            />
          </div>
        </div>

        {tabs.map(({ value, content }) => (
          <TabsContent key={value} value={value}>
            {content}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default JobStatus;
